import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { clearToken, getToken, setToken, setUnauthorizedHandler } from '../api/client.js';
import * as authApi from '../api/auth.js';
import { getSubscription } from '../api/payments.js';

const AuthContext = createContext(null);

/**
 * Holds the session: the token, the account, and the current subscription.
 *
 * On mount it revalidates any stored token against /auth/me, so a token that
 * expired while the tab was closed results in the login screen rather than an
 * app shell that fails to load anything.
 */
export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [subscription, setSubscription] = useState(null);
  // 'loading' until the stored token has been checked — routes wait on this so
  // a signed-in user never sees a flash of the login screen on refresh.
  const [status, setStatus] = useState(getToken() ? 'loading' : 'signed-out');

  const signOutLocally = useCallback(() => {
    clearToken();
    setUser(null);
    setSubscription(null);
    setStatus('signed-out');
  }, []);

  // Any 401 from any request lands here.
  useEffect(() => {
    setUnauthorizedHandler(signOutLocally);
    return () => setUnauthorizedHandler(null);
  }, [signOutLocally]);

  const refreshSubscription = useCallback(async () => {
    try {
      const res = await getSubscription();
      setSubscription(res.subscription ?? null);
      return res.subscription ?? null;
    } catch {
      // A failed subscription lookup shouldn't sign anyone out; treat the user
      // as free until the next refresh succeeds.
      return null;
    }
  }, []);

  // Revalidate a stored token once, at startup.
  useEffect(() => {
    if (!getToken()) return;

    let cancelled = false;
    (async () => {
      try {
        const res = await authApi.getMe();
        if (cancelled) return;
        setUser(res.user);
        setStatus('signed-in');
        refreshSubscription();
      } catch {
        // client.js has already cleared the token on a 401.
        if (!cancelled) signOutLocally();
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [refreshSubscription, signOutLocally]);

  /** Called by the login screen once /auth/verify returns a token. */
  const signIn = useCallback(
    (token, account) => {
      setToken(token);
      setUser(account ?? null);
      setStatus('signed-in');
      refreshSubscription();
    },
    [refreshSubscription]
  );

  const signOut = useCallback(async () => {
    try {
      await authApi.logout();
    } catch {
      // The token is stateless server-side, so a failed call changes nothing —
      // dropping it locally is what actually ends the session.
    }
    signOutLocally();
  }, [signOutLocally]);

  const value = useMemo(() => {
    const tier = subscription?.status === 'paid' ? subscription.tier : 'free';
    return {
      user,
      status,
      isSignedIn: status === 'signed-in',
      subscription,
      tier,
      isPaid: subscription?.status === 'paid',
      hasTopPicks: subscription?.status === 'paid' && subscription.tier === 'premium',
      signIn,
      signOut,
      refreshSubscription,
    };
  }, [user, status, subscription, signIn, signOut, refreshSubscription]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
